import { api } from "./client";
import type { GamePack, GamePackFile } from "@/types";

// ── Côté enseignant ────────────────────────────────────────────────────────────

export const listPacks = (courseId: number) =>
  api.get<GamePack[]>(`/api/instructor/courses/${courseId}/packs`);

export const createPack = (courseId: number, name: string, description = "") =>
  api.post<GamePack>(`/api/instructor/courses/${courseId}/packs`, { name, description });

export const updatePack = (packId: number, data: { name?: string; description?: string }) =>
  api.patch<GamePack>(`/api/instructor/packs/${packId}`, data);

export const deletePack = (packId: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/packs/${packId}`);

export const getPackFile = (packId: number, path: string) =>
  api.get<GamePackFile>(`/api/instructor/packs/${packId}/files/${path}`);

export const upsertPackFile = (packId: number, path: string, content: string) =>
  api.put<GamePackFile>(`/api/instructor/packs/${packId}/files/${path}`, { content });

export const deletePackFile = (packId: number, path: string) =>
  api.delete<{ ok: boolean }>(`/api/instructor/packs/${packId}/files/${path}`);

// ── Assets binaires (Sprint 5.C bonus) ─────────────────────────────────────────

export interface PackAsset {
  id: number;
  path: string;
  mime_type: string;
  size_bytes: number;
  content_hash: string;
}

export const listPackAssets = (packId: number) =>
  api.get<PackAsset[]>(`/api/instructor/packs/${packId}/assets`);

export const uploadPackAsset = async (
  packId: number, file: File, path?: string,
): Promise<PackAsset> => {
  const form = new FormData();
  form.append("file", file);
  if (path) form.append("path", path);
  const res = await fetch(`/api/instructor/packs/${packId}/assets`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? err.description ?? res.statusText);
  }
  return res.json();
};

export const deletePackAsset = (packId: number, assetId: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/packs/${packId}/assets/${assetId}`);

// ── Côté élève (runtime, appelé au chargement d'une étape) ────────────────────

export interface RuntimePack {
  id: number;
  name: string;
  files: Record<string, string>;  // path → contenu
  /** Sprint 5.C — chemin asset → SHA1. Permet au frontend de construire les
   *  URLs versionnées `/api/exercises/packs/<id>/assets/<path>?v=<hash>` pour
   *  cache-busting PWA quand le prof remplace un asset. */
  assets_manifest: Record<string, string>;
}

export const getStepPack = (stepId: number) =>
  api.get<{ pack: RuntimePack | null }>(`/api/exercises/steps/${stepId}/pack`);


// ── Import bundle JSON (packs + séries en une fois) ──────────────────────────

/** Comportement quand un nom du bundle clashe avec un pack/série existant.
 *  - "refuse"  (défaut) : 409 + liste, rien d'écrit.
 *  - "replace" : supprime l'ancien (et ses fichiers/étapes) avant d'écrire.
 *  - "rename"  : suffixe l'élément du bundle (`_2`, ` (2)`…) pour cohabiter. */
export type ConflictStrategy = "refuse" | "replace" | "rename";

export interface ImportBundle {
  version: 1;
  conflict_strategy?: ConflictStrategy;
  packs?: Array<{
    name: string;
    description?: string;
    files: Record<string, string>;
  }>;
  series?: Array<{
    title: string;
    description?: string;
    published?: boolean;
    steps: Array<{
      title?: string;
      instructions?: string;
      starter_code?: string;
      validation_type?: "manual" | "auto_test" | "both";
      test_code?: string;
      solution_code?: string;
      scaffold_pre?: string;
      scaffold_post?: string;
      pack_name?: string;
    }>;
  }>;
}

export interface ImportResult {
  ok: true;
  strategy: ConflictStrategy;
  packs_created: number;
  series_created: number;
  steps_created: number;
  renamed: { packs: Record<string, string>; series: Record<string, string> };
}

export const importBundle = (courseId: number, bundle: ImportBundle) =>
  api.post<ImportResult>(`/api/instructor/courses/${courseId}/import-bundle`, bundle);

/** Sprint 5.C bonus — déclenche le téléchargement du bundle JSON du cours.
 *  Utilise fetch (et pas notre client `api`) car la réponse est un fichier
 *  attaché et on veut récupérer le `Content-Disposition` pour le nom propre.
 *  Renvoie le nom de fichier proposé par le serveur (ou null si bug). */
export async function downloadCourseBundle(courseId: number): Promise<string | null> {
  const res = await fetch(`/api/instructor/courses/${courseId}/export-bundle`, {
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? err.description ?? res.statusText);
  }
  // Extrait le filename du header (les navigateurs ne le respectent pas pour
  // les Blobs téléchargés via <a download>, on doit le faire à la main).
  const cd = res.headers.get("Content-Disposition") || "";
  const m  = cd.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : `cours-${courseId}-bundle.json`;
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
}
